import React from "react";
import { Box, Flex, FlexProps } from "../../primitives";
import { IconFromSet, IconValue } from "../../icons";
import { Button } from "@cmp-src/primitives/button";

/** A button of the empty view. */
export interface EmptyViewAction {
  label: string;
  onClick: () => void;
  /** Shown before the label. */
  icon?: IconValue | React.FC;
}

export interface EmptyViewProps extends Omit<FlexProps, "title"> {
  /** Icon to display above the title/message */
  icon?: IconValue | React.FC;
  /** Main heading text */
  title?: React.ReactNode;
  /** Secondary description text */
  message?: React.ReactNode;
  /** Action buttons */
  actions?: EmptyViewAction[];
  /** Remove border and background from container */
  noBackground?: boolean;
}

/**
 * What a list or section shows when it has nothing yet: an icon, a title, a message and
 * the actions that fill it, in a dashed box unless `noBackground`.
 */
export const EmptyView: React.FC<EmptyViewProps> = ({
  icon,
  title,
  message,
  actions,
  noBackground,
  ...props
}) => {
  return (
    <Flex
      {...props}
      className={[
        "w-full flex-col items-center justify-center gap-4 p-10",
        !noBackground &&
          "rounded-lg border border-neutral-300 border-dashed bg-neutral-50/50",
        props.className
      ]}
    >
      {!!icon && (
        <Box className="flex items-center justify-center rounded-full bg-neutral-100 p-4 text-neutral-400">
          <IconFromSet icon={icon} size={8} />
        </Box>
      )}
      {(title || message) && (
        <Box className="text-center">
          {!!title && (
            <Box className="text-lg font-semibold text-content-strong">{title}</Box>
          )}
          {!!message && (
            <Box className="mt-1 text-sm text-content">{message}</Box>
          )}
        </Box>
      )}
      {!title && !message && (
        <Box className="text-center text-content">No data</Box>
      )}
      {!!actions?.length && (
        <Flex className={"mt-2 gap-2"}>
          {actions.map((action, index) => (
            <Button
              key={String(index)}
              onClick={action.onClick}
              className={"px-4 py-2"}
              iconLeft={action.icon as never}
            >
              {action.label}
            </Button>
          ))}
        </Flex>
      )}
    </Flex>
  );
};
